// Port of webapp/src/blocks/block.ts

import {Utils} from '../common/utils'

export const contentBlockTypes = [
    'text', 'image', 'divider', 'checkbox', 'h1', 'h2', 'h3',
    'list-item', 'attachment', 'quote', 'video',
] as const

export const blockTypes = [...contentBlockTypes, 'board', 'view', 'card', 'comment', 'attachment', 'unknown'] as const

export type ContentBlockTypes = typeof contentBlockTypes[number]
export type BlockTypes = typeof blockTypes[number]

export interface BlockPatch {
    parentId?: string
    schema?: number
    type?: BlockTypes
    title?: string
    updatedFields?: Record<string, unknown>
    deletedFields?: string[]
    deleteAt?: number
}

export interface Block {
    id: string
    boardId: string
    parentId: string
    createdBy: string
    modifiedBy: string

    schema: number
    type: BlockTypes
    title: string
    fields: Record<string, any>

    createAt: number
    updateAt: number
    deleteAt: number

    limited?: boolean
}

export interface FileInfo {
    url?: string
    archived?: boolean
    extension?: string
    name?: string
    size?: number
}

export function createBlock(block?: Partial<Block>): Block {
    const now = Date.now()
    return {
        id: block?.id || Utils.createBlockId(block?.type),
        schema: 1,
        boardId: block?.boardId || '',
        parentId: block?.parentId || '',
        createdBy: block?.createdBy || '',
        modifiedBy: block?.modifiedBy || '',
        type: block?.type || 'unknown',
        fields: block?.fields ? {...block.fields} : {},
        title: block?.title || '',
        createAt: block?.createAt || now,
        updateAt: block?.updateAt || now,
        deleteAt: block?.deleteAt || 0,
        limited: Boolean(block?.limited),
    }
}

const difference = (a: string[], b: string[]): string[] => a.filter((v) => !b.includes(v))

/** Returns [patch, undoPatch] exactly like upstream createPatchesFromBlocks(). */
export function createPatchesFromBlocks(newBlock: Block, oldBlock: Block): [BlockPatch, BlockPatch] {
    const build = (next: Block, prev: Block): BlockPatch => {
        const updatedFields: Record<string, unknown> = {}
        Object.keys(next.fields).forEach((key) => {
            if (prev.fields[key] !== next.fields[key]) {
                updatedFields[key] = next.fields[key]
            }
        })
        const updatedData: Record<string, unknown> = {}
        Object.keys(next).forEach((key) => {
            if (key !== 'fields' && (next as any)[key] !== (prev as any)[key]) {
                updatedData[key] = (next as any)[key]
            }
        })
        return {
            ...updatedData,
            updatedFields,
            deletedFields: difference(Object.keys(prev.fields), Object.keys(next.fields)),
        } as BlockPatch
    }
    return [build(newBlock, oldBlock), build(oldBlock, newBlock)]
}
